import { useEffect, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { PODIUM_URL, PODIUM_SCALE, DRACO_DECODER_PATH } from "./constants";

// Nodes de placement : Place_1, place_2, Place-3... Tolérant sur la casse et le
// séparateur pour ne pas dépendre des conventions de l'export Blender.
const PLACE_RE = /^place[_-]?(\d+)$/i;

// Empty optionnel servant de point de vue. Un vrai node caméra glTF est utilisé
// en priorité ; cet alias couvre le cas fréquent où l'export Blender a été fait
// sans cocher "Cameras" et où on pose juste un empty repère.
const VIEW_RE = /^(camera|cam|view|viewpoint)([_-].*)?$/i;

export interface PodiumView {
  // Exprimés dans le repère du podium, déjà mis à l'échelle PODIUM_SCALE.
  position: THREE.Vector3;
  target: THREE.Vector3;
  // Renseigné seulement si le GLB embarque une vraie caméra glTF.
  fov?: number;
}

export interface PodiumData {
  node: THREE.Object3D;
  // Offsets des places par rapport à la racine du podium, DÉJÀ mis à l'échelle
  // PODIUM_SCALE. Clé = rang (1 = Place_1).
  spots: Map<number, THREE.Vector3>;
  // Point de vue auteur, si le GLB en fournit un. Sinon la caméra retombe sur un
  // cadrage calculé à partir des places occupées.
  view: PodiumView | null;
}

/*
 * usePodium : charge le GLB du podium à la main plutôt qu'avec useGLTF.
 *
 * Raison : le podium est optionnel. useGLTF passe par Suspense et propage
 * l'erreur à l'ErrorBoundary le plus proche ; il n'y en a aucun dans le <Canvas>,
 * donc un fichier absent ou illisible ferait sauter TOUTE la scène 3D en pleine
 * partie. Ici un échec laisse simplement podium à null et la fin de partie
 * retombe sur l'ancien zoom cinématique sur le winner.
 *
 * L'asset est compressé Draco (KHR_draco_mesh_compression est dans
 * extensionsRequired), d'où le DRACOLoader branché sur le décodeur auto-hébergé.
 */
export function usePodium(): PodiumData | null {
  const [data, setData] = useState<PodiumData | null>(null);

  useEffect(() => {
    let cancelled = false;

    const draco = new DRACOLoader().setDecoderPath(DRACO_DECODER_PATH);
    const loader = new GLTFLoader().setDRACOLoader(draco);

    loader
      .loadAsync(PODIUM_URL)
      .then((gltf) => {
        if (cancelled) return;
        const node = gltf.scene;
        node.updateWorldMatrix(true, true);

        // Inverse de la matrice monde de la racine : permet d'exprimer chaque
        // repère dans le repère du podium même s'il est parenté à un sous-objet.
        const rootInverse = new THREE.Matrix4().copy(node.matrixWorld).invert();
        const toPodiumSpace = (obj: THREE.Object3D) =>
          new THREE.Vector3()
            .setFromMatrixPosition(obj.matrixWorld)
            .applyMatrix4(rootInverse)
            .multiplyScalar(PODIUM_SCALE);

        const spots = new Map<number, THREE.Vector3>();
        const markers: THREE.Object3D[] = [];
        let gltfCamera: THREE.Camera | null = null;
        let viewEmpty: THREE.Object3D | null = null;

        node.traverse((obj) => {
          if ((obj as THREE.Camera).isCamera) {
            gltfCamera = obj as THREE.Camera;
            return;
          }
          const match = PLACE_RE.exec(obj.name);
          if (match) {
            spots.set(Number(match[1]), toPodiumSpace(obj));
            markers.push(obj);
            return;
          }
          if (!viewEmpty && VIEW_RE.test(obj.name) && !(obj as THREE.Mesh).isMesh) {
            viewEmpty = obj;
          }
        });

        // Un empty Blender s'exporte sans mesh et ne rend rien. Mais si les
        // repères ont été modélisés (cube, sphère), il ne faut pas les voir sur
        // l'écran du kiosque : on masque le mesh du marqueur, pas ses enfants.
        for (const m of markers) {
          if ((m as THREE.Mesh).isMesh) (m as THREE.Mesh).visible = false;
        }

        // Point de vue : caméra glTF en priorité, sinon empty repère. Dans les
        // deux cas on dérive la cible en projetant l'axe -Z local (convention
        // glTF/Blender : une caméra regarde son -Z).
        let view: PodiumView | null = null;
        const source: THREE.Object3D | null = gltfCamera || viewEmpty;
        if (source) {
          const position = toPodiumSpace(source);
          const forward = new THREE.Vector3(0, 0, -1)
            .applyQuaternion(source.getWorldQuaternion(new THREE.Quaternion()))
            .normalize();
          const target = position
            .clone()
            .add(forward.multiplyScalar(PODIUM_SCALE * 4));
          const persp = source as THREE.PerspectiveCamera;
          view = { position, target, fov: persp.isPerspectiveCamera ? persp.fov : undefined };
        }

        node.traverse((obj) => {
          if ((obj as THREE.Mesh).isMesh) {
            obj.castShadow = true;
            obj.receiveShadow = true;
          }
        });

        if (spots.size === 0) {
          console.warn(
            `[Podium] ${PODIUM_URL} chargé mais aucun node Place_<n> trouvé. ` +
              "Nomme les repères Place_1, Place_2, Place_3... dans le GLB."
          );
        }

        setData({ node, spots, view });
      })
      .catch((err) => {
        // Fichier absent, illisible, ou extension non supportée : la fin de
        // partie garde le comportement d'avant, sans casser la scène.
        console.warn(`[Podium] ${PODIUM_URL} non chargé, fin de partie classique.`, err);
      })
      .finally(() => {
        draco.dispose();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return data;
}

interface PodiumProps {
  data: PodiumData;
  // Position monde de la racine du podium.
  position: [number, number, number];
}

export function Podium({ data, position }: PodiumProps) {
  return <primitive object={data.node} position={position} scale={PODIUM_SCALE} />;
}
